import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Text,
  VStack,
  HStack,
  Input,
  Button,
  Spinner,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  Badge,
  Tabs,
  TabList,
  Tab,
  InputGroup,
  InputLeftElement,
  Divider,
  useToast,
} from '@chakra-ui/react';
import { Search, UserPlus, UserCheck, Send, Users, Star, Wifi, Link, Copy, Share2 } from 'lucide-react';
import { 
  getAllUsers, 
  searchUsers, 
  UserProfile, 
  followUser, 
  unfollowUser,
  getFollowing,
  getFriends,
  getUserProfile,
} from '../services/socialService';
import { sendInvite, generateSecureInviteToken, InviteTokenData } from '../services/inviteService';
import { isUserOnline, getActiveUsersList } from '../services/presenceService';
import { POKER_COLORS } from '../theme/designSystem';

interface UserSearchProps {
  isOpen: boolean;
  onClose: () => void;
  currentUid: string;
  currentUserName: string;
  tableId?: string;
  tableName?: string;
  isPrivate?: boolean; //  NEW: For private table badge

  defaultTab?: number; //  NEW: Default tab to open (0=All, 1=Online, 2=Friends)

}

interface EnhancedUser extends UserProfile {
  isOnline: boolean;
  isFriend: boolean;
  isFollowingThem: boolean;
  currentTable: string | null;
}

const UserSearch: React.FC<UserSearchProps> = ({
  isOpen,
  onClose,
  currentUid,
  currentUserName,
  tableId,
  tableName,
  isPrivate = false,
  defaultTab = 0, //  Default to "All" tab

}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [users, setUsers] = useState<EnhancedUser[]>([]);
  const [loading, setLoading] = useState(false);
  const [inviteSent, setInviteSent] = useState<{[key: string]: boolean}>({});
  const [activeTab, setActiveTab] = useState(0);
  const navigate = useNavigate();
  const toast = useToast();

  // ============================================================================
  //  INVITE LINK STATE

  // ============================================================================
  const [linkCopied, setLinkCopied] = useState(false);
  const [generatingLink, setGeneratingLink] = useState(false);

  // ============================================================================
  //  COPY INVITE LINK HANDLER

  // ============================================================================
  const handleCopyInviteLink = async () => {
    if (!tableId || !tableName) return;
    
    setGeneratingLink(true);
    try {
      // Generate secure token
      const tokenData: InviteTokenData = await generateSecureInviteToken(tableId);
      
      // Create shareable message
      const message = `Join me at ${tableName}!\n\n${tokenData.shareUrl}`;

      
      // Copy to clipboard
      await navigator.clipboard.writeText(message);
      
      setLinkCopied(true);
      toast({
        title: 'Invite Link Copied!',

        description: 'Share this link to invite anyone to your table',
        status: 'success',
        duration: 3000,
      });
      
      // Reset after 3 seconds
      setTimeout(() => setLinkCopied(false), 3000);
    } catch (error: any) {
      console.error('Error generating invite link:', error);
      toast({
        title: 'Failed to generate link',
        description: error.message || 'Please try again',
        status: 'error',
        duration: 3000,
      });
    } finally {
      setGeneratingLink(false);
    }
  };

  // ============================================================================
  //  NATIVE SHARE HANDLER (Mobile)

  // ============================================================================
  const handleShareInviteLink = async () => {
    if (!tableId || !tableName) return;
    
    setGeneratingLink(true);
    try {
      // Generate secure token
      const tokenData: InviteTokenData = await generateSecureInviteToken(tableId);
      
      if (navigator.share) {
        await navigator.share({
          title: `Join ${tableName}`,
          text: `Join me for poker at ${tableName}! `,

          url: tokenData.shareUrl,
        });
        toast({
          title: 'Shared!',

          status: 'success',
          duration: 2000,
        });
      } else {
        // Fallback to copy
        await handleCopyInviteLink();
      }
    } catch (error: any) {
      // User may have cancelled - don't show error for AbortError
      if (error.name !== 'AbortError') {
        console.error('Error sharing:', error);
      }
    } finally {
      setGeneratingLink(false);
    }
  };

  // Check if native share is available
  const canNativeShare = typeof navigator.share === 'function';

  //  Navigate to user's public profile

  const handleViewProfile = (userId: string) => {
    onClose(); // Close the modal first
    navigate(`/profile/${userId}`);
  };

  const loadUsers = useCallback(async () => {
    if (!currentUid) return;
    
    setLoading(true);
    try {
      // 1. Get all users or search results
      let results: UserProfile[];
      if (searchQuery.length >= 2) {
        results = await searchUsers(searchQuery);
      } else {
        results = await getAllUsers(50);
      }
      
      // Filter out current user
      results = results.filter(u => u.userId !== currentUid);
      console.log('[UserSearch] Found users:', results.length);

      // 2. Get following list
      let followingIds = new Set<string>();
      try {
        const following = await getFollowing(currentUid);
        followingIds = new Set(following.map(f => f.userId));
        console.log('[UserSearch] Following count:', followingIds.size);
      } catch (err) {
        console.error('[UserSearch] Error getting following:', err);
      }
      
      // 3. Get friends (mutual follows)
      let friendIds = new Set<string>();
      try {
        const friends = await getFriends(currentUid);
        friendIds = new Set(friends.map(f => f.userId));
        console.log('[UserSearch] Friends count:', friendIds.size);
      } catch (err) {
        console.error('[UserSearch] Error getting friends:', err);
      }
      
      // 4. Get online users - try bulk fetch first, then individual fallback
      const onlineMap = new Map<string, { currentTable: string | null }>();
      
      try {
        // Try to get active users list
        const activeUsers = await getActiveUsersList(100);
        console.log('[UserSearch] Active users from presence:', activeUsers.length);
        
        activeUsers.forEach(u => {
          if (u.userId !== currentUid) {
            onlineMap.set(u.userId, { currentTable: u.currentTable });
          }
        });
      } catch (err) {
        console.error('[UserSearch] Error getting active users list:', err);
      }

      // If bulk fetch returned nothing, check each user individually (slower but more reliable)
      if (onlineMap.size === 0 && results.length <= 20) {
        console.log('[UserSearch] Checking online status individually...');
        for (const usr of results) {
          try {
            const online = await isUserOnline(usr.userId);
            if (online) {
              onlineMap.set(usr.userId, { currentTable: null });
              console.log('[UserSearch] User online:', usr.username);
            }
          } catch (err) {
            // Ignore individual check errors
          }
        }
      }

      console.log('[UserSearch] Total online users found:', onlineMap.size);

      // Presence is the source of truth for "online": anyone online who is
      // not already in the loaded list gets their profile fetched and added,
      // so the Online tab always agrees with the online counter.
      onlineMap.delete('recorder-bot');
      if (searchQuery.length < 2) {
        const known = new Set(results.map(u => u.userId));
        const missing = Array.from(onlineMap.keys()).filter(id => !known.has(id) && id !== currentUid);
        const fetched = await Promise.all(missing.map(id => getUserProfile(id).catch(() => null)));
        fetched.forEach(p => { if (p && p.userId) results.unshift(p); });
      }

      // 5. Enhance users with online/friend status
      const enhancedUsers: EnhancedUser[] = results.map(usr => ({
        ...usr,
        isOnline: onlineMap.has(usr.userId),
        isFriend: friendIds.has(usr.userId),
        isFollowingThem: followingIds.has(usr.userId),
        currentTable: onlineMap.get(usr.userId)?.currentTable || null,
      }));

      // 6. Sort: Online friends first → Online users → Offline friends → Others
      enhancedUsers.sort((a, b) => {
        // Priority 1: Online friends
        if (a.isOnline && a.isFriend && !(b.isOnline && b.isFriend)) return -1;
        if (b.isOnline && b.isFriend && !(a.isOnline && a.isFriend)) return 1;
        
        // Priority 2: Online users
        if (a.isOnline && !b.isOnline) return -1;
        if (b.isOnline && !a.isOnline) return 1;
        
        // Priority 3: Friends (offline)
        if (a.isFriend && !b.isFriend) return -1;
        if (b.isFriend && !a.isFriend) return 1;
        
        // Priority 4: Following
        if (a.isFollowingThem && !b.isFollowingThem) return -1;
        if (b.isFollowingThem && !a.isFollowingThem) return 1;
        
        return 0;
      });

      setUsers(enhancedUsers);
    } catch (error) {
      console.error('[UserSearch] Error loading users:', error);
    } finally {
      setLoading(false);
    }
  }, [searchQuery, currentUid]);

  //  UPDATED: Load users when modal opens + respect defaultTab

  useEffect(() => {
    if (isOpen) {
      setActiveTab(defaultTab); // Use defaultTab instead of hardcoded 0
      loadUsers();
    } else {
      // Reset when modal closes
      setSearchQuery('');
      setActiveTab(0);
      setLinkCopied(false);
    }
  }, [isOpen, loadUsers, defaultTab]);

  // Debounced search
  useEffect(() => {
    if (!isOpen) return;
    
    const timer = setTimeout(() => {
      loadUsers();
    }, 300);
    
    return () => clearTimeout(timer);
  }, [searchQuery, isOpen, loadUsers]);

  // Refresh online status periodically while modal is open
  useEffect(() => {
    if (!isOpen) return;
    
    const interval = setInterval(() => {
      loadUsers();
    }, 30000); // Refresh every 30 seconds
    
    return () => clearInterval(interval);
  }, [isOpen, loadUsers]);

  const handleFollow = async (targetUid: string) => {
    try {
      const user = users.find(u => u.userId === targetUid);
      if (!user) return;
      
      if (user.isFollowingThem) {
        await unfollowUser(currentUid, targetUid);
      } else {
        await followUser(currentUid, targetUid);
      }
      
      // Refresh the list
      loadUsers();
    } catch (error) {
      console.error('Error toggling follow:', error);
    }
  };

  const handleInvite = async (targetUid: string) => {
    if (!tableId || !tableName) return;
    try {
      await sendInvite(currentUid, currentUserName, targetUid, tableId, tableName);
      setInviteSent(prev => ({ ...prev, [targetUid]: true }));
      setTimeout(() => {
        setInviteSent(prev => ({ ...prev, [targetUid]: false }));
      }, 3000);
    } catch (error) {
      console.error('Error sending invite:', error);
    }
  };

  // Filter users based on tab
  const getFilteredUsers = () => {
    switch (activeTab) {
      case 1: // Online only
        return users.filter(u => u.isOnline);
      case 2: // Friends only
        return users.filter(u => u.isFriend);
      default: // All
        return users;
    }
  };

  const filteredUsers = getFilteredUsers();
  const onlineCount = users.filter(u => u.isOnline).length;
  const friendCount = users.filter(u => u.isFriend).length;

  const renderUserCard = (usr: EnhancedUser) => (
    <Box
      key={usr.userId}
      p={{ base: 2, md: 3 }}
      bg={usr.isOnline ? `${POKER_COLORS.cyan}11` : `${POKER_COLORS.darkTeal}22`}
      borderRadius="10px"
      border={`1px solid ${usr.isFriend ? '#22c55e44' : usr.isOnline ? `${POKER_COLORS.cyan}44` : `${POKER_COLORS.cyan}22`}`}
      transition="all 0.2s"
      _hover={{ bg: `${POKER_COLORS.darkTeal}44` }}
    >
      {/* Top row: Avatar + Username + Badges */}
      <HStack 
        spacing={{ base: 2, md: 3 }}
        cursor="pointer"
        onClick={() => handleViewProfile(usr.userId)}
        _hover={{ opacity: 0.8 }}
        mb={2}
      >
        {/* Avatar with online indicator */}
        <Box position="relative" flexShrink={0}>
          <Box
            w={{ base: "36px", md: "44px" }}
            h={{ base: "36px", md: "44px" }}
            borderRadius="full"
            bg={usr.isFriend 
              ? 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)' 
              : `linear-gradient(135deg, ${POKER_COLORS.cyan} 0%, ${POKER_COLORS.darkTeal} 100%)`
            }
            display="flex"
            alignItems="center"
            justifyContent="center"
            border={usr.isOnline ? '2px solid #22c55e' : 'none'}
          >
            <Text color="white" fontWeight="bold" fontSize={{ base: "md", md: "lg" }}>
              {usr.username?.charAt(0).toUpperCase() || '?'}
            </Text>
          </Box>
          
          {/* Online indicator dot */}
          {usr.isOnline && (
            <Box
              position="absolute"
              bottom="0"
              right="0"
              w={{ base: "10px", md: "14px" }}
              h={{ base: "10px", md: "14px" }}
              bg="#22c55e"
              borderRadius="full"
              border="2px solid"
              borderColor={POKER_COLORS.cardBg}
              boxShadow="0 0 8px #22c55e"
            />
          )}
        </Box>

        {/* User info - flex to fill space */}
        <VStack align="flex-start" spacing={0} flex={1} minW={0}>
          <HStack spacing={1} maxW="100%">
            <Text 
              color="white" 
              fontWeight="bold" 
              fontSize={{ base: "xs", md: "sm" }}
              isTruncated
              maxW={{ base: "100px", md: "150px" }}
            >
              {usr.username}
            </Text>
            {usr.isFriend && (
              <Badge colorScheme="green" fontSize="8px" px={1}>
                <HStack spacing={0.5}>
                  <Star size={8} />
                  <Text>Friend</Text>
                </HStack>
              </Badge>
            )}
          </HStack>
          
          <HStack spacing={1}>
            {usr.isOnline ? (
              usr.currentTable ? (
                <Badge colorScheme="yellow" fontSize="9px">
                   In Game

                </Badge>
              ) : (
                <Badge colorScheme="green" fontSize="9px">
                  <HStack spacing={0.5}>
                    <Wifi size={8} />
                    <Text>Online</Text>
                  </HStack>
                </Badge>
              )
            ) : (
              <Text color="gray.500" fontSize="xs">Offline</Text>
            )}
            <Text color="gray.500" fontSize="xs">
               {usr.handsWon || 0}

            </Text>
          </HStack>
        </VStack>
      </HStack>

      {/* Bottom row: Action buttons */}
      <HStack spacing={2} justify="flex-end">
        <Button
          size="xs"
          leftIcon={usr.isFollowingThem ? <UserCheck size={12} /> : <UserPlus size={12} />}
          bg={usr.isFollowingThem ? `${POKER_COLORS.cyan}44` : 'transparent'}
          border={`1px solid ${POKER_COLORS.cyan}`}
          color={POKER_COLORS.cyan}
          _hover={{ bg: `${POKER_COLORS.cyan}22` }}
          onClick={(e) => {
            e.stopPropagation();
            handleFollow(usr.userId);
          }}
          px={{ base: 2, md: 3 }}
          fontSize={{ base: "xs", md: "sm" }}
        >
          {usr.isFollowingThem ? 'Following' : 'Follow'}
        </Button>

        {tableId && (
          <Button
            size="xs"
            leftIcon={<Send size={12} />}
            bg={inviteSent[usr.userId] ? '#22c55e' : usr.isOnline ? POKER_COLORS.cyan : 'gray.600'}
            color={usr.isOnline ? POKER_COLORS.darkTeal : 'white'}
            _hover={{ opacity: 0.9 }}
            onClick={(e) => {
              e.stopPropagation();
              handleInvite(usr.userId);
            }}
            isDisabled={inviteSent[usr.userId]}
            px={{ base: 2, md: 3 }}
            fontSize={{ base: "xs", md: "sm" }}
          >
            {inviteSent[usr.userId] ? 'Sent!' : 'Invite'}
          </Button>
        )}
      </HStack>
    </Box>
  );

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg">
      <ModalOverlay bg="blackAlpha.800" />
      <ModalContent bg={POKER_COLORS.cardBg} border={`1px solid ${POKER_COLORS.cyan}44`} maxH="85vh">
        <ModalHeader color={POKER_COLORS.cyan} pb={2}>
          <HStack justify="space-between">
            <HStack>
              <Search size={20} />
              <Text>Find Players</Text>
            </HStack>
            <HStack spacing={3}>
              <Badge colorScheme="green" fontSize="sm" px={2} py={1}>
                <HStack spacing={1}>
                  <Box w="8px" h="8px" bg="#22c55e" borderRadius="full" />
                  <Text>{onlineCount} Online</Text>
                </HStack>
              </Badge>
            </HStack>
          </HStack>
        </ModalHeader>
        <ModalCloseButton color={POKER_COLORS.cyan} />
        
        <ModalBody pb={6} display="flex" flexDirection="column">
          {/* ============================================================ */}
          {/*  INVITE LINK SECTION - Copy/Share Secure Link */}

          {/* ============================================================ */}
          {tableId && tableName && (
            <>
              <Box
                p={4}
                bg={POKER_COLORS.darkBase || '#0D1B2A'}
                borderRadius="xl"
                border="1px solid"
                borderColor={`${POKER_COLORS.cyan}44`}
                mb={4}
              >
                <VStack spacing={3} align="stretch">
                  {/* Header */}
                  <HStack justify="space-between" align="center">
                    <HStack spacing={2}>
                      <Link size={18} color={POKER_COLORS.cyan} />
                      <Text color="white" fontWeight="bold" fontSize="md">
                        Invite Link
                      </Text>
                    </HStack>
                    {isPrivate && (
                      <Badge
                        colorScheme="purple"
                        px={2}
                        py={1}
                        borderRadius="md"
                        fontSize="xs"
                      >
                         Private Access

                      </Badge>
                    )}
                  </HStack>

                  {/* Description */}
                  <Text color="gray.400" fontSize="sm" textAlign="center">
                    Share this link to invite anyone - even new users!
                    {isPrivate && ' No private code needed.'}
                  </Text>

                  {/* Action Buttons */}
                  <HStack spacing={3}>
                    {/* Copy Button */}
                    <Button
                      flex={1}
                      onClick={handleCopyInviteLink}
                      isLoading={generatingLink}
                      loadingText="Generating..."
                      bg={linkCopied ? '#22c55e' : POKER_COLORS.cyan}
                      color={linkCopied ? 'white' : 'black'}
                      _hover={{ 
                        bg: linkCopied ? '#16a34a' : '#00c4c4',
                        transform: 'translateY(-1px)',
                      }}
                      _active={{ transform: 'translateY(0)' }}
                      leftIcon={linkCopied ? <UserCheck size={16} /> : <Copy size={16} />}
                      height="44px"
                      fontWeight="bold"
                    >
                      {linkCopied ? 'Copied!' : 'Copy Link'}
                    </Button>

                    {/* Native Share Button (mobile) */}
                    {canNativeShare && (
                      <Button
                        flex={1}
                        onClick={handleShareInviteLink}
                        isLoading={generatingLink}
                        bg="transparent"
                        border="2px solid"
                        borderColor={POKER_COLORS.cyan}
                        color={POKER_COLORS.cyan}
                        _hover={{ 
                          bg: `${POKER_COLORS.cyan}22`,
                          transform: 'translateY(-1px)',
                        }}
                        _active={{ transform: 'translateY(0)' }}
                        leftIcon={<Share2 size={16} />}
                        height="44px"
                        fontWeight="bold"
                      >
                        Share
                      </Button>
                    )}
                  </HStack>

                  {/* Expiry note */}
                  <Text color="gray.500" fontSize="xs" textAlign="center">
                    Link expires in 24 hours
                  </Text>
                </VStack>
              </Box>

              {/* Divider with "OR" */}
              <HStack spacing={3} mb={4}>
                <Divider borderColor="gray.600" />
                <Text color="gray.500" fontSize="sm" whiteSpace="nowrap">
                  or invite directly
                </Text>
                <Divider borderColor="gray.600" />
              </HStack>
            </>
          )}

          {/* Search Input */}
          <InputGroup mb={3}>
            <InputLeftElement pointerEvents="none">
              <Search size={18} color="gray" />
            </InputLeftElement>
            <Input
              placeholder="Search by username..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              bg={`${POKER_COLORS.darkTeal}44`}
              border={`1px solid ${POKER_COLORS.cyan}44`}
              color="white"
              pl={10}
              _focus={{ borderColor: POKER_COLORS.cyan }}
            />
          </InputGroup>

          {/* Tabs */}
          <Tabs 
            variant="soft-rounded" 
            colorScheme="cyan" 
            size="sm" 
            mb={3}
            index={activeTab}
            onChange={setActiveTab}
          >
            <TabList>
              <Tab 
                color="gray.400" 
                _selected={{ bg: `${POKER_COLORS.cyan}33`, color: POKER_COLORS.cyan }}
              >
                <HStack spacing={1}>
                  <Users size={14} />
                  <Text>All ({users.length})</Text>
                </HStack>
              </Tab>
              <Tab 
                color="gray.400" 
                _selected={{ bg: 'green.900', color: '#22c55e' }}
              >
                <HStack spacing={1}>
                  <Wifi size={14} />
                  <Text>Online ({onlineCount})</Text>
                </HStack>
              </Tab>
              <Tab 
                color="gray.400" 
                _selected={{ bg: 'green.900', color: '#22c55e' }}
              >
                <HStack spacing={1}>
                  <Star size={14} />
                  <Text>Friends ({friendCount})</Text>
                </HStack>
              </Tab>
            </TabList>
          </Tabs>

          {/* User List */}
          {loading ? (
            <VStack py={8}>
              <Spinner color={POKER_COLORS.cyan} />
              <Text color="gray.500" fontSize="sm">Loading players...</Text>
            </VStack>
          ) : filteredUsers.length === 0 ? (
            <VStack py={8}>
              <Text color="gray.500">
                {activeTab === 1 ? 'No online players found' : 
                 activeTab === 2 ? 'No friends yet. Follow users who follow you back!' :
                 'No users found'}
              </Text>
              {activeTab === 0 && users.length === 0 && (
                <Text color="gray.600" fontSize="sm">
                  Be the first to invite friends!
                </Text>
              )}
              {activeTab === 1 && (
                <Text color="gray.600" fontSize="sm">
                  Invite friends to play!
                </Text>
              )}
            </VStack>
          ) : (
            <VStack 
              spacing={2} 
              align="stretch" 
              overflowY="auto" 
              flex={1}
              maxH="300px"
              pr={2}
              css={{
                '&::-webkit-scrollbar': { width: '6px' },
                '&::-webkit-scrollbar-track': { background: 'transparent' },
                '&::-webkit-scrollbar-thumb': { background: POKER_COLORS.cyan + '44', borderRadius: '3px' },
              }}
            >
              {filteredUsers.map(renderUserCard)}
            </VStack>
          )}

          {/* Hint for inviting */}
          {tableId && filteredUsers.length > 0 && (
            <Box mt={3} p={2} bg={`${POKER_COLORS.cyan}11`} borderRadius="md">
              <Text color="gray.400" fontSize="xs" textAlign="center">
                 Online players will receive your invite instantly!

              </Text>
            </Box>
          )}
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export default UserSearch;